import {
  calculateExpressionSync,
  setExpressionFunctionCachedResultSync,
} from '@lmt/expressions';

import {
  AbsenceType,
  AppointmentEventDto,
  AppointmentType,
  CalendarConfigurationDto,
  CalendarType,
  DayOfWeek,
  JsQuery,
  NoteDto,
  PatchVisitDto,
  QueryFactory,
  ResourceDto,
  ResourceType,
  RoundingType,
  VisitDto,
  VisitListDto,
} from '@lmt/shared-types';

import { DXDS } from '@lmt/unicorn-shared/app/common/devexpress/dx-data-source';
import type { AppointmentModel } from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-react-scheduler';
import {
  AppointmentUpdatingEvent,
  SchedulerRef,
  useDxScheduler,
} from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-scheduler';
import { ResourceInfo } from '@lmt/unicorn-shared/app/components/calendar/calendar.types';
import { useFirstDayOfWeek } from '@lmt/unicorn-shared/app/components/calendar/common';
import {
  convertPresentDaysToExcludedDays,
  defaultVisitColor,
  HolidaysAndAnonimized,
} from '@lmt/unicorn-shared/app/components/calendar/devextreme/common';
import {
  AppointmentPopup,
  AppointmentPopupProps,
} from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/AppointmentPopup';
import { DayViewCalendarHeader } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/DayViewCalendarHeader';
import {
  AppointmentItem,
  ResourceViewAppointment,
} from '@lmt/unicorn-shared/app/components/calendar/devextreme/resource-view/ResourceViewAppointment';
import {
  ResourceViewScheduler,
  SchedulerProps,
} from '@lmt/unicorn-shared/app/components/calendar/devextreme/resource-view/ResourceViewScheduler';
import {
  getHoursFromSeconds,
  getQueriesFromVisitCardSettings,
} from '@lmt/unicorn-shared/app/components/calendar/helper';
import { ListType } from '@lmt/unicorn-shared/app/components/calendar/PatientAndWaitingListNavigation';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { AppPopper } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppPopper';
import { isAnyObjectInDataSourceSuitFilter } from '@lmt/unicorn-shared/app/components/documents/components/data-source-helpers';
import { calendarParams } from '@lmt/unicorn-shared/app/constants/calendarParams';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { useSubtenantsDict } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { emptyArray, isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { saveAsFile } from '@lmt/unicorn-shared/app/helpers/download-files';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { triggerDownloadFileDialog } from '@lmt/unicorn-shared/app/helpers/file-helper';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import {
  addDays,
  addHours,
  addSeconds,
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import Logger from 'js-logger';
import { bindPopper } from 'material-ui-popup-state';
import { usePopupState } from 'material-ui-popup-state/hooks';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReportVisitInfo } from '../report/components/common/types';
import { createReport } from '../report/report-integration';
import { getVisitCardStylesForCalendarType } from '../visit-card-settings/components/common';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { getBGColor } from '../color-settings/components/color-parser';
import { useEventCallback } from '@mui/material';
import { ignoreSignalRChanges } from '@lmt/unicorn-shared/app/components/service/startup/signalr/SignalRHandler';

const defaultStartDayHour = 9;
const defaultEndDayHour = 19;

type Props = {
  containerClass?: string;
  currentDate: Date;
  calendarType: CalendarType;
  configuration: CalendarConfigurationDto;
  isConfigurationLoading?: boolean;
  setExportEnabled: (value: boolean) => void;
  isExportData: boolean;
  setExportData: (value: boolean) => void;
  setShownListType: (value: ListType) => void;
  shownListType: ListType;
};

export function getRoundedStartTime(start: Date, roundingType: RoundingType) {
  switch (roundingType) {
    case RoundingType.None:
      return start;
    case RoundingType.RoundingTo12Hours:
      const midNight = startOfDay(start);
      const midDay = addHours(midNight, 12);
      return start >= midDay ? midDay : midNight;
    case RoundingType.RoundingTo24Hours:
      return startOfDay(start);
  }
}

export function getRoundedEndTime(
  end: Date,
  roundingType: RoundingType,
  isMultiDayVisitRounding: boolean,
  start: Date,
) {
  const midNight = startOfDay(end);
  if (isMultiDayVisitRounding && start.getDate() !== end.getDate()) {
    return midNight;
  }
  switch (roundingType) {
    case RoundingType.None:
      return end;
    case RoundingType.RoundingTo12Hours:
      const midDay = addHours(midNight, 12);
      if (end > midNight) {
        return end > midDay ? addDays(midNight, 1) : midDay;
      }
      return !start || end > start ? end : midDay;
    case RoundingType.RoundingTo24Hours:
      const nextMidnight = addDays(midNight, 1);
      if (end > midNight) {
        return nextMidnight;
      }
      return !start || end > start ? end : nextMidnight;
  }
}

export const Calendar: FC<Props> = React.memo(function Calendar(props) {
  const {
    currentDate,
    calendarType,
    configuration,
    isExportData,
    setExportData,
    setExportEnabled,
    setShownListType,
  } = props;
  const i18n = useTranslation();
  useDxScheduler();
  const resourcesQuery = QueryFactory.ResourceQuery.useGetResourcesQuery();
  const actualResources = resourcesQuery.data?.data ?? emptyArray();
  const modals = useModal();
  const visitTypes = useVisitTypes();
  const colorSettingsQuery =
    QueryFactory.AppointmentColorSettingsQuery.useGetQuery();
  const visitCardSettingsQuery =
    QueryFactory.VisitCardSettingsQuery.useGetQuery();
  const isNightTheme = useUniSelector(getSettings).isNightTheme;
  const schedulerRef = useRef<SchedulerRef>(null);
  const queryCache = useQueryClient();

  const subtenants = useSubtenantsDict();
  const isTabActive = true;
  const schedulerKeyRef = useRef(1);
  useMemo(() => {
    if (isTabActive) {
      schedulerKeyRef.current = schedulerKeyRef.current + 1;
    }
  }, [isTabActive]);
  const [shownResourcesList, setShownResourcesList] = useState<ResourceInfo[]>(
    [],
  );

  const [parsedQueryString] = useQueryParamsInTab(calendarParams);
  const [visitsAsData, setVisitsAsData] = useState<AppointmentModel[]>([]);
  const firstDayOfWeekIndex = useFirstDayOfWeek();
  const getPeriodForDate = useCallback(
    (date: Date, calendarType: CalendarType): { start: Date; end: Date } => {
      switch (calendarType) {
        case CalendarType.Day:
          return { start: startOfDay(date), end: endOfDay(date) };
        case CalendarType.Week:
          return {
            start: startOfWeek(date, {
              weekStartsOn: firstDayOfWeekIndex,
            }),
            end: endOfWeek(date, {
              weekStartsOn: firstDayOfWeekIndex,
            }),
          };
        case CalendarType.Month:
          return { start: startOfMonth(date), end: endOfMonth(date) };
      }
    },
    [firstDayOfWeekIndex],
  );

  const currentPeriod = useMemo(() => {
    return getPeriodForDate(currentDate, calendarType);
  }, [currentDate, calendarType, getPeriodForDate]);

  const [customQueries, setCustomQueries] = useState<JsQuery[] | null>(null);
  useMemo(() => {
    getQueriesFromVisitCardSettings(
      getVisitCardStylesForCalendarType(
        configuration.calendarType,
        visitCardSettingsQuery.data,
      ),
    )
      .then((x) => setCustomQueries(x))
      .catch((e) => Logger.error(e));
  }, [visitCardSettingsQuery.data, configuration.calendarType]);

  const filteredDoctors = useMemo(() => {
    const doctors = actualResources?.filter(
      (item) => item.type === ResourceType.Doctors,
    );
    if (!parsedQueryString.doctors.length)
      return doctors ?? emptyArray<ResourceDto>();
    return (
      doctors?.filter((doctor) =>
        parsedQueryString.doctors.includes(doctor.id),
      ) ?? emptyArray<ResourceDto>()
    );
  }, [actualResources, parsedQueryString.doctors?.join(',')]);
  const filteredPlaces = useMemo(() => {
    const places = actualResources?.filter(
      (item) => item.type === ResourceType.Places,
    );
    if (!parsedQueryString.places.length)
      return places ?? emptyArray<ResourceDto>();
    return (
      places?.filter((place) => parsedQueryString.places.includes(place.id)) ??
      emptyArray<ResourceDto>()
    );
  }, [actualResources, parsedQueryString.places?.join(',')]);
  const filteredSpecialConditions = useMemo(() => {
    const specialConditions = actualResources?.filter(
      (item) => item.type === ResourceType.SpecialConditions,
    );
    if (!parsedQueryString.specialConditions.length)
      return specialConditions ?? emptyArray<ResourceDto>();
    return (
      specialConditions?.filter((place) =>
        parsedQueryString.specialConditions.includes(place.id),
      ) ?? emptyArray<ResourceDto>()
    );
  }, [actualResources, parsedQueryString.specialConditions?.join(',')]);
  function recalculateExpressions(appointment: any) {
    (appointment as any).expressions = getVisitCardStylesForCalendarType(
      configuration.calendarType,
      visitCardSettingsQuery.data,
    ).map((line) => {
      const key = setExpressionFunctionCachedResultSync(
        appointment.calculatedQueries as any,
      );
      return calculateExpressionSync(line.expression, key);
    });
    return appointment;
  }

  const {
    data: visits,
    isFetching,
    isLoading: isLoading,
  } = QueryFactory.AppointmentEventsQuery.useSearchAppointmentsQuery(
    {
      from: currentPeriod.start,
      to: currentPeriod.end,
      eventTypes: parsedQueryString.appointmentTypes as number[],
      subtenants: parsedQueryString.subtenants as number[],
      tags: parsedQueryString.tags as number[],
      visitTypes: parsedQueryString.visitTypes as number[],
      visitStatuses: parsedQueryString.visitStatuses as number[],
      resourceIds: configuration.isResourceViewCalendar
        ? ([
            // if parsedQueryString.places/doctors/specialConditions are empty, we filter for ALL places/doctors/conditions
            // otherwise if you select a single doctor you will receive no appointments with places
            ...(configuration.isResourceViewCalendar &&
            parsedQueryString.places.length === 0
              ? filteredPlaces.map((x) => x.id)
              : parsedQueryString.places!),
            ...(configuration.isResourceViewCalendar &&
            parsedQueryString.specialConditions.length === 0
              ? filteredSpecialConditions.map((x) => x.id)
              : parsedQueryString.specialConditions!),
            ...(configuration.isResourceViewCalendar &&
            parsedQueryString.doctors.length === 0
              ? filteredDoctors.map((x) => x.id)
              : parsedQueryString.doctors!),
          ] as number[])
        : undefined,
      placesIds: configuration.isResourceViewCalendar
        ? undefined
        : (parsedQueryString.places as number[]),
      specialConditionsIds: configuration.isResourceViewCalendar
        ? undefined
        : (parsedQueryString.specialConditions as number[]),
      doctorIds: configuration.isResourceViewCalendar
        ? undefined
        : (parsedQueryString.doctors as number[]),
      queries: customQueries,
    },
    {
      enabled: !!customQueries,
      structuralSharing(oldData: any, newData: any) {
        if (!oldData || !newData) return newData;
        if (oldData.length != newData.length) return newData;
        return JSON.stringify(oldData) != JSON.stringify(newData)
          ? newData
          : oldData;
      },
    },
  );
  const isRounding =
    configuration.isResourceViewCalendar &&
    (configuration.roundingType !== RoundingType.None ||
      configuration.isMultiDayVisitRounding);

  // Bug 61846: Appointment returns to one of the previous positions after a calendar config change
  const [needRefetch, setNeedRefetch] = useState(false);
  useEffect(() => {
    if (!needRefetch) {
      return;
    }
    void queryCache.invalidateQueries({
      queryKey:
        QueryFactory.AppointmentEventsQuery.searchAppointmentsQueryKey(),
    });
    setNeedRefetch(false);
  }, [configuration]);

  /**
   * Visits from all subtenants (including unavailable subtenants) that have resources
   */
  const { data: appointmentsWithResources } =
    QueryFactory.AppointmentEventsQuery.useSearchAppointmentsWithResourceOccupationQuery(
      {
        from: currentPeriod.start,
        to: currentPeriod.end,
        isWithoutCanceledVisits: true,
      },
      {
        //enabled: !!configuration.isResourceViewCalendar,
      },
    );

  const availableSubtenants = useMemo(() => {
    return Object.values(subtenants).map((x) => x.id);
  }, [subtenants]);

  /**
   * Absences (without holidays) also visits from unavailable subtenants that use resources
   * Visits are displayed as hatched rectangles without any information (except occupied resources)
   * Absences are also displayed as hatched rectangles with general information (holiday title, participants, etc.)
   */
  const absencesAndAnonimyzedAppointments: HolidaysAndAnonimized[] =
    useMemo(() => {
      return (
        appointmentsWithResources
          ?.filter(
            (x) =>
              !availableSubtenants.some((id) => id === x.subtenantId) &&
              (!configuration.showAbsences ? x.absenceType === null : true) &&
              (configuration.isResourceViewCalendar
                ? true
                : x.absenceType === AbsenceType.Holiday),
          )
          ?.map((x) => ({
            id: x.id,
            startDate: isRounding
              ? getRoundedStartTime(x.start, configuration.roundingType)
              : x.start,
            endDate: isRounding
              ? getRoundedEndTime(
                  x.end,
                  configuration.roundingType,
                  configuration.isMultiDayVisitRounding,
                  x.start,
                )
              : x.end,
            resources: x.resourceIds ?? [],
            isAnonymized: !x.absenceType, // only visits from unavailable subtenants are anonymized
            absenceType: x.absenceType,
            title: x.title ?? '',
            // we need to manually set `allDay`, because Holidays could be longer than 1 day, but we don't want to move them to `All Day` panel
            allDay: false,
          }))
          .sort((x) => (x.absenceType === AbsenceType.Holiday ? 1 : -1)) ?? []
      );
    }, [
      availableSubtenants,
      appointmentsWithResources,
      configuration,
      shownResourcesList,
      actualResources,
    ]);

  /*
   * We need to adjust an expression for the 'is blank' and 'is not blank' conditions.
   * These conditions only works for null value, we want to use them for empty values as well.
   * Examples expressions:
   * ['fieldName', '=', null]
   * ['fieldName', '<>', '']
   * [['fieldName1', '=', null], 'and', ['fieldName2', '>', '3']]
   *
   * If we need to use null and empty string values for 'is blank',
   * we need to adjust the condition as follows:
   * ['fieldName', '=', null] => [['fieldName', '=', null], 'and', ['fieldName', '=', '']]
   *
   * If we use 'is not blank', the condition should be adjusted as follows
   * ['fieldName', '<>', null] => [['fieldName', '<>', null], 'or', ['fieldName', '<>', '']]
   */
  const processFilter = (expressions?: any | undefined) => {
    if (typeof expressions === 'object' && expressions && expressions.length) {
      if (expressions.includes(null)) {
        const copy = [...expressions];
        expressions[0] = copy;
        expressions[1] = copy[1] === '=' ? 'or' : 'and';
        expressions[2] = [...copy];
        expressions[2][2] = '';
      } else {
        return expressions.map((expression: any) => {
          return processFilter(expression);
        });
      }
    }

    return expressions;
  };

  /**
   * Create appointment model in calendar from visit dto
   */
  const createAppointmentModel = useCallback(
    (visit: VisitListDto | VisitDto): AppointmentModel => {
      const start = isRounding
        ? getRoundedStartTime(visit.start, configuration.roundingType)
        : visit.start;
      const end = isRounding
        ? getRoundedEndTime(
            visit.end,
            configuration.roundingType,
            configuration.isMultiDayVisitRounding,
            visit.start,
          )
        : visit.end;
      const dataSource = new DXDS.DataSource([visit]);
      const fittingRule = (colorSettingsQuery.data?.rules ?? []).find(
        (rule) => {
          let parsedFilterExpressions = (rule as any)
            .parsedFilterExpression as any;

          parsedFilterExpressions = processFilter(
            JSON.parse(JSON.stringify(parsedFilterExpressions)),
          );

          return isAnyObjectInDataSourceSuitFilter(
            dataSource,
            parsedFilterExpressions,
          );
        },
      );
      dataSource.dispose();
      const visitTitle = i18n.t(
        (visitTypes.find((visitType) => visitType.id === visit.typeId)?.value ||
          '-') as any,
      );

      return {
        id: visit.id,
        startDate: start,
        endDate: end,
        data: { ...visit, title: visitTitle },
        resources: visit.resourceIds,
        patient: {
          id: visit.patientId,
          fullName: visit.patientName,
        },
        color: getBGColor(
          fittingRule?.color ?? defaultVisitColor,
          isNightTheme,
        ),
        subtenantCode: subtenants[visit.subtenantId]?.code,
        lineStyles: getVisitCardStylesForCalendarType(
          configuration.calendarType,
          visitCardSettingsQuery.data,
        ),
        // we need to manually set `allDay`, because Holidays could be longer than 1 day, but we don't want to move them to `All Day` panel
        // `allDay` needs to be `false` in Resource view, otherwise it causes Bug #35847
        allDay: configuration.isResourceViewCalendar
          ? false
          : differenceInDays(visit.end, visit.start) >= 1,
      };
    },
    [
      configuration,
      colorSettingsQuery.data,
      visitCardSettingsQuery.data,
      visitTypes,
      isNightTheme,
    ],
  );

  const [dxLoading, setDxLoading] = useState(false);
  useEffect(() => {
    if (!isTabActive) {
      return;
    }
    // this is a performance optimization for fixing Bug 56485: Too slow appointments loading on calendar config changing
    setVisitsAsData([]);
    if (!visits) return;
    if (!visits?.length) return;

    setDxLoading(true);
    setTimeout(() => {
      const visitsWithExpressions = visits.map((appointment) =>
        recalculateExpressions({ ...appointment }),
      );
      const visitsData = visitsWithExpressions
        ? (
            visitsWithExpressions.filter(
              (x) => x.appointmentType !== AppointmentType.Absence,
            ) as VisitListDto[]
          ).map((visit) => createAppointmentModel(visit))
        : [];
      setVisitsAsData(visitsData);
      setDxLoading(false);
    });
  }, [
    visits,
    configuration,
    shownResourcesList,
    createAppointmentModel,
    isTabActive,
    i18n.i18n.language,
  ]);

  /**
   * Get existing visit model if it's already in calendar or get visit from server and create new appointment model
   */
  const getOrCreateAppointmentModel = useCallback(
    async (visitId: number) => {
      let visitData = visitsAsData.find((x) => x.id === visitId);
      if (!visitData) {
        const visit =
          await QueryFactory.AppointmentEventsClient.getVisit(visitId);
        if (!visit) {
          return undefined;
        }
        visitData = createAppointmentModel(visit);
      }
      return visitData;
    },
    [visitsAsData, createAppointmentModel],
  );

  /**
   * Replace old appointment model in calendar with given one (comparison by id)
   */
  const replaceAppointmentModel = useEventCallback(
    (appointment: AppointmentModel) => {
      setVisitsAsData((oldVisits) => [
        ...oldVisits.filter((x) => x.id !== appointment.id),
        appointment,
      ]);
    },
  );

  const updateVisit = useEventCallback(
    async (
      visitId: number,
      newVisitData: PatchVisitDto,
      oldVisitData: AppointmentModel,
      e: AppointmentUpdatingEvent,
    ): Promise<void> => {
      const model = e.newData?.data as AppointmentEventDto;
      const oldModel = { ...model };

      const appoint = await getOrCreateAppointmentModel(
        oldVisitData.id as number,
      )!;

      function setNewValues(
        appointmentModel: AppointmentModel,
        start: Date | null | undefined,
        end: Date | null | undefined,
        model: AppointmentEventDto | PatchVisitDto,
      ) {
        appointmentModel.endDate = end!;
        appointmentModel.startDate = start!;
        appointmentModel.resources = model.resourceIds!;

        if (appointmentModel.data.calculatedQueries) {
          appointmentModel.data.calculatedQueries.visit['']['End'] =
            end?.toISOString();
          appointmentModel.data.calculatedQueries.visit['']['Start'] =
            start?.toISOString();
          recalculateExpressions(appointmentModel.data);
        }

        schedulerRef.current
          ?.instance()
          .updateAppointment(appointmentModel as any, {
            // Bug 61390: Appointment is displayed incorrectly (wrong position) after moving in Resource view
            startDate: getRoundedStartTime(
              appointmentModel!.startDate as any,
              configuration.roundingType,
            ),
            endDate: getRoundedEndTime(
              appointmentModel!.endDate as any,
              configuration.roundingType,
              configuration.isMultiDayVisitRounding,
              appointmentModel!.startDate as any,
            ),
          });
        Object.assign(e.newData.data, model);
      }
      async function revert() {
        setNewValues(appoint as any, oldModel.start, oldModel.end, oldModel);
      }
      try {
        const unignoreSignalRChanges = ignoreSignalRChanges(
          'Visit',
          addSeconds(new Date(), 10),
          {
            ignoreFunction(change) {
              return (
                change.object.id.toString() === oldVisitData.id?.toString()
              );
            },
          },
        );

        setNewValues(
          appoint as any,
          newVisitData.start,
          newVisitData.end,
          newVisitData,
        );
        await QueryFactory.AppointmentEventsClient.updateVisit(
          visitId,
          newVisitData,
        );
        setNeedRefetch(true);
        setTimeout(() => {
          unignoreSignalRChanges();
        }, 1000);
      } catch (e: any) {
        if (e.status === 409) {
          const acceptConflict = await showConflictDialog();
          if (acceptConflict) {
            newVisitData.acceptConflict = true;
            const unignoreSignalRChanges = ignoreSignalRChanges(
              'Visit',
              addSeconds(new Date(), 10),
              {
                ignoreFunction(change) {
                  return (
                    change.object.id.toString() === oldVisitData.id?.toString()
                  );
                },
              },
            );

            await QueryFactory.AppointmentEventsClient.updateVisit(
              visitId,
              newVisitData,
            );
            setTimeout(() => {
              unignoreSignalRChanges();
            }, 1000);
          } else {
            // if conflict was not accepted we return visit to its initial place
            revert();
            return;
          }
        } else {
          revert();
          if (e.status === 403) {
            await modals.showError({ text: parseError(e.response) });
            return;
          }
          throw e;
        }
      }
    },
  );

  const showConflictDialog = useEventCallback(() =>
    modals.showConfirm({
      title: i18n.t('event.visit.already_taken_title'),
      text: i18n.t('event.visit.already_taken_text'),
      okButtonText: i18n.t('event.visit.save_anyway'),
    }),
  );

  useEffect(() => {
    setExportEnabled(!isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (isExportData) {
      setExportEnabled(false);
      exportReport().catch((e) => console.error('exportReport', e));
    }

    async function exportReport() {
      try {
        const data: ReportVisitInfo[] = visitsAsData.map((visitData) => {
          const dataSource = new DXDS.DataSource([visitData.data]);
          const fittingRule = (colorSettingsQuery.data?.rules ?? [])
            .filter(
              (rule) =>
                !rule.filterExpression ||
                rule.filterExpression.includes('typeId'),
            )
            .find((rule) => {
              const parsedFilterExpressions = (rule as any)
                .parsedFilterExpression as any;

              return isAnyObjectInDataSourceSuitFilter(
                dataSource,
                parsedFilterExpressions,
              );
            });
          const doctorName = actualResources.find(
            (resource) =>
              resource.type === ResourceType.Doctors &&
              visitData.resources?.includes(resource.id),
          )?.name;
          dataSource.dispose();
          return {
            startDate: visitData.data.start,
            endDate: visitData.data.end,
            patientName: visitData.patient.fullName,
            doctorName: doctorName ?? '',
            visitTypeId: visitData.data.typeId,
            color: fittingRule?.color ?? defaultVisitColor,
          };
        });
        const blob = await createReport({
          currentPeriod,
          data,
          calendarType,
          visitTypes,
          firstDayOfWeekIndex,
        });

        const fileName = i18n.t(configuration.name, {
          defaultValue: configuration.name,
        });
        if (isElectron()) {
          await saveAsFile(`${fileName}.pdf`, blob);
        } else {
          triggerDownloadFileDialog(blob, fileName);
        }
      } catch (error) {
        await modals.showError({ text: parseError(error) });
      } finally {
        setExportData(false);
        setExportEnabled(true);
      }
    }
  }, [isExportData]);

  const excludedDays: DayOfWeek[] = useMemo(() => {
    if (!configuration.weekDays?.length) {
      return [];
    }
    return convertPresentDaysToExcludedDays(configuration.weekDays);
  }, [configuration]);

  /**
   * Shown appointments in resource view including anonymized (shown as hatch)
   */
  const shownVisits = useMemo(() => {
    return [
      ...visitsAsData,
      // do not show holidays as events (we will change the Cell background instead)
      ...(configuration.calendarType === CalendarType.Month
        ? []
        : absencesAndAnonimyzedAppointments.filter(
            (x) => x.absenceType !== AbsenceType.Holiday,
          )),
    ];
  }, [
    visitsAsData,
    absencesAndAnonimyzedAppointments,
    configuration.calendarType,
  ]);

  const [appointmentPopupProps, setAppointmentPopupProps] =
    useState<AppointmentPopupProps | null>(null);
  const popupState = usePopupState({
    variant: 'popper',
    popupId: 'appointment-popup',
  });
  const popupStateRef = useRef(popupState);
  popupStateRef.current = popupState;

  const appointmentComponent = useCallback(
    (props: AppointmentItem) => {
      return (
        <ResourceViewAppointment
          {...props}
          onMouseLeave={(e) => {
            popupStateRef.current.onMouseLeave(e);
          }}
          onMouseOver={(e) => {
            if (!popupStateRef.current.isOpen) popupStateRef.current.open(e);
          }}
          onAppointmentEnter={(data) => {
            setAppointmentPopupProps(data);
          }}
        />
      );
    },
    [i18n.i18n.language, customQueries],
  );

  const calendarProps: Partial<SchedulerProps> &
    Pick<SchedulerProps, 'currentView'> = useMemo(() => {
    if (configuration.isResourceViewCalendar) {
      return {
        currentView:
          calendarType === CalendarType.Day ? 'timelineDay' : 'timelineWeek',
      };
    }
    const endDayHour =
      getHoursFromSeconds(configuration.endOfDayInSecondsSinceMidnight) ??
      defaultEndDayHour;
    let startDayHour =
      getHoursFromSeconds(configuration.startOfDayInSecondsSinceMidnight) ??
      defaultStartDayHour;
    if (startDayHour > endDayHour) {
      startDayHour = 0;
    }
    return {
      startDayHour: startDayHour,
      endDayHour: endDayHour,
      currentView:
        calendarType === CalendarType.Day
          ? 'day'
          : calendarType === CalendarType.Week
            ? 'week'
            : 'month',
    };
  }, [calendarType, configuration]);
  const holidays = useMemo(() => {
    const holidays = absencesAndAnonimyzedAppointments.filter(
      (x) => x.absenceType === AbsenceType.Holiday,
    );

    const result: Record<string, HolidaysAndAnonimized[]> = {};
    holidays.forEach((item) => {
      let date = startOfDay(item.startDate);
      while (date < item.endDate) {
        const key = format(date, 'yyyy-MM-dd');
        if (!result[key]) result[key] = [];
        result[key].push(item);
        date = addDays(date, 1);
      }
    });
    return result;
  }, [absencesAndAnonimyzedAppointments]);

  const notesQuery = QueryFactory.NoteQuery.useGetQuery({
    from: currentPeriod.start,
    to: currentPeriod.end,
  });
  const notes = useMemo(() => {
    const result: Record<string, NoteDto[]> = {};
    notesQuery.data?.data
      ?.sort((x) => (x.urgent ? -1 : 1))
      .forEach((item) => {
        if (!item.startDate) {
          return;
        }
        let date = item.startDate;
        do {
          const key = format(date, 'yyyy-MM-dd');
          if (!result[key]) result[key] = [];
          result[key].push(item);
          date = addDays(date, 1);
        } while (item.endDate && date <= item.endDate);
      });
    return result;
  }, [notesQuery.data]);

  return (
    <div className={props.containerClass} data-test-id={'calendar-content'}>
      <Loading
        loading={isLoading || !!props.isConfigurationLoading || dxLoading}
        title={i18n.t('calendar.filter.loading')}
      >
        {calendarType === CalendarType.Day && (
          <DayViewCalendarHeader
            currentDate={currentDate}
            holidays={holidays}
            notes={notes}
          />
        )}
        <ResourceViewScheduler
          {...calendarProps}
          schedulerRef={schedulerRef}
          key={schedulerKeyRef.current}
          appointmentComponent={appointmentComponent}
          appointments={shownVisits}
          holidays={holidays}
          places={filteredPlaces}
          specialConditions={filteredSpecialConditions}
          doctors={filteredDoctors}
          currentDate={props.currentDate}
          configuration={configuration}
          shownResourcesList={shownResourcesList}
          setShownResourcesList={setShownResourcesList}
          getOrCreateAppointmentModel={getOrCreateAppointmentModel}
          updateVisit={updateVisit}
          replaceAppointmentModel={replaceAppointmentModel}
          excludedDays={excludedDays}
          notes={notes}
        />

        {appointmentPopupProps && (
          <AppPopper {...bindPopper(popupState)} delay={400}>
            <AppointmentPopup
              {...appointmentPopupProps}
              onClose={popupState.close}
              setShownListType={setShownListType}
            />
          </AppPopper>
        )}
      </Loading>
    </div>
  );
});
